/* eslint-disable max-len */
import styled from 'styled-components';
import { AllieChip } from '../../../components/Chip';
import { AllieText, TextType } from '../../../components/Text';
import { AllieEllipse } from '../../../components/Ellipse/Ellipse';
import { MulticolorBarStyled } from '../../../components/ProgressIndicators/MulticolorBar/MulticolorBar.styles';
import { getCssVar, isTablet } from '../../../common/utils';
import { mediaQueries } from '../../../common/mediaQueries';

const mapProductionStatusChipBorder: Record<string, string> = {
  in_progress: '1.5px solid var(--allie-color-primary-60)',
  done: '1.5px solid var(--allie-color-neutral-60)',
  paused: '1.5px solid var(--allie-color-neutral-60)',
};

export const InfoOP = styled.div<{ isMobile?: boolean }>`
  display: flex;
  align-items: ${(props) => (props.isMobile || isTablet ? 'flex-start' : 'center')};
  flex-direction: ${(props) => (props.isMobile ? 'column' : 'row')};
  margin: ${(props) =>
    props.isMobile ? 'var(--allie-spacing-lg) 0' : 'var(--allie-spacing-md) 0'};
  gap: var(--allie-spacing-lg);
  column-gap: 45px;

  ${mediaQueries.tablet} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--allie-spacing-md);
  }
`;

export const InfoOPV2 = styled.div<{ isMobile?: boolean }>`
  display: flex;
  align-items: ${(props) => (props.isMobile ? 'flex-start' : 'center')};
  flex-direction: ${(props) => (props.isMobile ? 'column' : 'row')};
  margin: ${(props) =>
    props.isMobile ? 'var(--allie-spacing-lg) 0' : 'var(--allie-spacing-md) 0'};
  gap: var(--allie-spacing-lg);
`;

export const DataOP = styled.div`
  display: flex;
  align-items: center;
  column-gap: 12px;
  margin-top: 44px;
  justify-content: space-between;

  > div {
    display: flex;
    align-items: center;
    column-gap: 12px;
  }
`;

export const ProductionOrderStyled = styled.div`
  width: 100%;
  box-sizing: border-box;
  ion-progress-bar {
    height: 24px;
    border-radius: 24px;
    margin-top: 32px;
    margin-bottom: 8px;
    --background: #e4e3fe;
  }
  ion-progress-bar::part(progress) {
    border-radius: 24px;
  }

  ion-card-header {
    padding-bottom: 8px;
  }

  ion-card-content {
    padding-bottom: 8px;
  }

  .allie-text-button-small {
    font-size: 14px;
  }

  .production-order {
    &-card {
      border-radius: 16px;
    }

    &__status-chip {
      margin: 0;
      height: 24px;
      cursor: default;
    }

    &__action-button {
      --border-width: 1px;
      --border-radius: 8px;
      --border-color: var(--allie-color-neutral-30);
      --padding-top: 6px;
      --padding-bottom: 6px;
      --padding-start: 6px;
      --padding-end: 6px;
      --color: var(--allie-color-neutral-60);
      --box-shadow: none;
      cursor: default;

      & span {
        height: 20px;
      }

      &.startOp {
        --background: var(--allie-color-primary-90);
        --border-color: var(--allie-color-primary-90);
        --color: var(--allie-color-light-main);
        & span {
          color: var(--allie-color-light-main);
        }
      }
    }
  }

  .hide-component {
    display: none;
  }

  & .no-tv {
    background-color: white;
    border: 1px solid #dde1e6;
    border-radius: 8px;
    box-shadow: none;
    margin: 0;
  }
`;

export const HeaderCard = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: var(--allie-spacing-lg);
`;

export const TitleCard = styled.div<{ isMobile?: boolean }>`
  display: flex;
  gap: var(--allie-spacing-sm);
  align-items: center;
  width: 100%;
  justify-content: ${({ isMobile }) => (isMobile ? 'space-between' : 'unset')};
`;

export const StatsItemValue = styled.div`
  display: flex;
  align-items: center;
  background-color: var(--allie-color-neutral-10);
  padding: 6px 8px;
  column-gap: 10px;
  color: var(--allie-color-neutral-60);
  border-radius: 8px;
`;

export const CardContent = styled.div<{ status?: string }>`
  padding-bottom: 8px;
  opacity: ${(props) => (props.status === 'done' ? 0.5 : undefined)};
`;

export const POProgressBar = styled.div<{ isTv?: boolean }>`
  border-radius: 100px;
  background-color: var(--allie-color-primary-10);
  margin-top: ${(props) => (props.isTv ? '10px' : '32px')};
  margin-bottom: ${(props) => (props.isTv ? '0px' : '8px')};
  display: ${(props) => (props.isTv ? 'flex' : 'block')};
  justify-content: ${(props) => (props.isTv ? 'flex-start' : 'initial')};
  align-items: ${(props) => (props.isTv ? 'center' : 'initial')};
  height: ${(props) => (props.isTv ? '35px' : 'initial')};

  ${MulticolorBarStyled} {
    width: 100%;
  }
`;

export const TitleStatus = styled.div`
  display: flex;
  column-gap: 24px;
  width: 100%;
  max-width: 50%;
  align-items: center;
  flex-shrink: 0;

  .order-value {
    word-wrap: anywhere;
  }
`;

export const TitleProduct = styled.div`
  display: flex;
  column-gap: 12px;
  align-items: center;
  overflow: hidden;

  ion-icon {
    color: var(--allie-color-neutral-60);
    flex-shrink: 0;
  }

  .no-wrap-ellipsis {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

export const AllieEllipseStyled = styled(AllieEllipse)`
  width: 8px;
  height: 8px;
  box-shadow: 0px 0px 0px 4px var(--allie-color-white);
  margin: 4px;
  flex-shrink: 0;
  padding-block: 4px;
  box-sizing: border-box;
`;

export const AllieChipStyled = styled(AllieChip)<{ status: string }>`
  height: 22px;
  border-radius: 50px;
  padding: 2px 6px;
  gap: 4px;
  font-size: 12px;
  cursor: default;
  border: ${({ status }) => mapProductionStatusChipBorder[status]};
`;

export const VelocityContainer = styled.div<{ isMobile?: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.isMobile ? 'column' : 'row')};
  padding: 6px 4px;
  margin-top: 6px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-radius: 4px;
  background: var(--allie-color-neutral-10);
`;

export const VelocityContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex: 1 0 0;
  cursor: default;

  & div {
    border: none;
  }

  .stroke-icon {
    stroke: none;
  }
  svg {
    height: 14px;
    width: 14px;
    fill: var(--allie-color-neutral-60);
  }
`;

export const VelocityChip = styled(AllieChip)`
  padding: 2px 6px;
  cursor: default;
`;

export const PopoverContent = styled.div`
  color: var(--allie-color-neutral-60);
`;

export const MobileActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--allie-spacing-md);
`;

export const StyledMainText = styled(AllieText)`
  font-size: 20px;
  line-height: 28px;

  @media (min-width: ${() => getCssVar('--breakpoint-desktop-lg', '1600px')}) {
    font-size: 24px !important;
    line-height: 33px !important;
  }

  @media (min-width: ${() => getCssVar('--breakpoint-desktop-xl', '1920px')}) {
    font-size: 28px !important;
    line-height: 37px !important;
  }
`;

export const StyledDescText = styled(AllieText)<{ fontType?: TextType }>`
  font-size: 12px;
  white-space: nowrap;

  @media (min-width: ${() => getCssVar('--breakpoint-desktop-lg', '1600px')}) {
    font-size: 14px !important;
    line-height: 19px !important;
  }

  @media (min-width: ${() => getCssVar('--breakpoint-desktop-xl', '1920px')}) {
    font-size: 16px !important;
    line-height: 22px !important;
  }
`;

export const StyledDescTextTV = styled(AllieText)`
  white-space: nowrap;
`;

export const ToolbarButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: var(--allie-spacing-md);

  > * {
    flex: 1;
  }
`;
